import { useEffect } from "react";
import useSWR from "swr";
import { api, mediaUrl } from "@/lib/api";
import { canonicalBase } from "@/components/site/Seo";
import { useSettings } from "@/context/SettingsContext";

const GIORNI = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const fetcher = (url) => api.get(url).then((r) => r.data);

// Dopo mezzanotte schema.org vuole comunque un orario dello stesso giorno.
const chiusura = (fine, inizio) => (fine <= inizio ? "23:59" : fine);

export const RestaurantSchema = () => {
  const { settings } = useSettings();
  const { data } = useSWR("/orari", fetcher, { revalidateOnFocus: false });

  useEffect(() => {
    const base = canonicalBase();
    const orari = (data?.items || []).map((s) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: `https://schema.org/${GIORNI[s.weekday]}`,
      opens: s.start_time,
      closes: chiusura(s.end_time, s.start_time),
    }));
    const immagini = [settings.hero_image, settings.story_image, settings.cuisine_image]
      .filter(Boolean)
      .map((src) => `${mediaUrl(src)}?w=1200`);
    const social = [settings.instagram, settings.facebook].filter(Boolean);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "restaurant-ld";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Restaurant",
      "@id": `${base}/#osteria`,
      name: "Osteria Aterrana",
      description: settings.seo_home_description || settings.tagline,
      url: base,
      telephone: settings.phone_link || "+393311091971",
      email: settings.email,
      vatID: "03083930648",
      image: immagini.length ? immagini : undefined,
      logo: `${base}/logo-osteria-aterrana.png`,
      servesCuisine: ["Cucina irpina", "Cucina campana", "Cucina italiana"],
      priceRange: "€€",
      currenciesAccepted: "EUR",
      address: {
        "@type": "PostalAddress",
        streetAddress: settings.address || "Via Madonna di Montevergine 34",
        addressLocality: "Montoro",
        addressRegion: "AV",
        addressCountry: "IT",
      },
      hasMap: settings.maps_url || undefined,
      sameAs: social.length ? social : undefined,
      hasMenu: `${base}/menu`,
      openingHoursSpecification: orari.length ? orari : undefined,
      acceptsReservations: settings.online_booking_enabled === false ? "False" : "True",
      potentialAction: {
        "@type": "ReserveAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${base}/prenota`,
          inLanguage: "it-IT",
          actionPlatform: [
            "http://schema.org/DesktopWebPlatform",
            "http://schema.org/MobileWebPlatform",
          ],
        },
        result: { "@type": "FoodEstablishmentReservation", name: "Prenotazione tavolo" },
      },
    });
    document.getElementById("restaurant-ld")?.remove();
    document.head.appendChild(ld);
    return () => document.getElementById("restaurant-ld")?.remove();
  }, [settings, data]);

  return null;
};

export default RestaurantSchema;
